import React, { useCallback, useEffect, useState } from 'react';
import block from 'bem-cn';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { actions as promoActions } from 'features/promotions';
import { languages } from 'services/locale';

import { useTournamentTimer } from 'shared/hooks/useTournamentTimer';
import { ROLE_CASHIER } from 'shared/utils/roleBasedLocale';

import mainEN from './img/main.webp';
import mainFR from './img/main-fr.webp';
import prizesEN from './img/prizes.png';
import prizesFR from './img/prizes-fr.png';
import bottomEN from './img/bottom.webp';
import bottomFR from './img/bottom-fr.webp';
import firstPrizeImage from './img/1.webp';
import secondPrizeImage from './img/2.webp';
import thirdPrizeImage from './img/3.webp';

import CashiersTournament3Modal from './CashiersTournament3Modal/CashiersTournament3Modal';
import './CashiersTournament3.scss';

const b = block('cashiers-tournament3');

const TOURNAMENT_START = '2026-08-26T00:00:00+01:00';
const TOURNAMENT_END = '2026-09-26T00:00:00+01:00';
const RATING_SIZE = 7;
const PRIZES_BY_PLACE = {
  1: { image: firstPrizeImage, name: 'iPhone 17 Pro Max' },
  2: { image: secondPrizeImage, name: 'iPhone 17 Pro' },
  3: { image: secondPrizeImage, name: 'iPhone 17 Pro' },
  4: { image: thirdPrizeImage, name: 'Playstation 5 Slim' },
  5: { image: thirdPrizeImage, name: 'Playstation 5 Slim' },
  6: { image: thirdPrizeImage, name: 'Playstation 5 Slim' },
  7: { image: thirdPrizeImage, name: 'Playstation 5 Slim' },
};

const CashiersTournament3 = () => {
  const dispatch = useDispatch();
  const role = useSelector(state => state.auth.role, shallowEqual);
  const { locale: currentLocale, lang } = useSelector(state => state.locale, shallowEqual);
  const {
    actionProcessing,
    points: { myPoints, cashiersList },
  } = useSelector(state => state.promotions, shallowEqual);

  const locale = currentLocale.cashiersTournament2;
  const { days, hours, minutes, state } = useTournamentTimer(TOURNAMENT_START, TOURNAMENT_END);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isFrench = lang === languages.FR;
  const mainImage = isFrench ? mainFR : mainEN;
  const prizesImage = isFrench ? prizesFR : prizesEN;
  const bottomImage = isFrench ? bottomFR : bottomEN;

  const handleChangeModalVisibility = useCallback(flag => setIsModalOpen(flag), []);

  useEffect(() => {
    if (role >= ROLE_CASHIER) dispatch(promoActions.getPoints());
  }, [dispatch, role]);

  const timerTitle = state === 'started'
    ? locale.endOfTheTournament
    : state === 'waitStart'
      ? locale.tournamentIsNotStarted
      : locale.tournamentIsEnded;

  const rating = cashiersList.slice(0, RATING_SIZE);
  const hasMyPoints = role === ROLE_CASHIER && myPoints !== null && myPoints !== undefined;
  const isRatingVisible = !actionProcessing
    && (rating.length > 0 || hasMyPoints)
    && (state === 'started' || state === 'ended');

  return (
    <div className={b()}>
      {isModalOpen && <CashiersTournament3Modal onClose={() => handleChangeModalVisibility(false)} />}

      <section className={b('main')}>
        <img className={b('section-image')} src={mainImage} alt={locale.promoPageTitle} />
        <button
          type="button"
          className={b('conditions-button')}
          aria-label={locale.mainText2}
          onClick={() => handleChangeModalVisibility(true)}
        />
        <div className={b('timer')} aria-label={timerTitle}>
          <div className={b('timer-item')}>
            <strong>{days}</strong>
            <span>{locale.days}</span>
          </div>
          <div className={b('timer-item')}>
            <strong>{hours}</strong>
            <span>{locale.hours}</span>
          </div>
          <div className={b('timer-item')}>
            <strong>{minutes}</strong>
            <span>{locale.minutes}</span>
          </div>
        </div>
      </section>

      <img className={b('section-image', { prizes: true })} src={prizesImage} alt={locale.prizesText2} />

      {isRatingVisible ? (
        <section className={b('rating')}>
          <img className={b('section-image')} src={bottomImage} alt={locale.top10} />

          {role === ROLE_CASHIER && (
            <div className={b('my-points')}>
              <span>{locale.points}</span>
              <strong>{myPoints ?? 0}</strong>
            </div>
          )}

          {rating.length > 0 ? (
            <ol className={b('rating-list')}>
              {rating.map(item => {
                const prize = PRIZES_BY_PLACE[item.place];

                return (
                  <li className={b('rating-item')} key={item.id}>
                    <span className={b('rating-place')}>{item.place}</span>
                    <span className={b('rating-cashier')}>
                      <small>{locale.cashierId}</small>
                      <strong>{item.id}</strong>
                    </span>
                    <span className={b('rating-prize-image')}>
                      <img src={prize.image} alt="" />
                    </span>
                    <span className={b('rating-prize')}>
                      <small>{locale.prize}</small>
                      <strong>{prize.name}</strong>
                    </span>
                    <span className={b('rating-points')}>
                      <small>{locale.point}</small>
                      <strong>{item.points}</strong>
                    </span>
                  </li>
                );
              })}
            </ol>
          ) : (
            <div className={b('rating-empty')}>{locale.ratingIsEmpty}</div>
          )}
        </section>
      ) : null}
    </div>
  );
};

export default CashiersTournament3;
